import { useState } from 'react'

import { ReactComponent as PlusIcon } from 'assets/icons/plus16.svg'
import { compact } from 'lodash/fp'
import { useFormikContext } from 'formik'

import { Button } from 'components/Button'
import { RecipientSelectorValue, TemplateFormData } from 'components/ReviewTemplateFormModal/Types'
import { useMemberOptions } from '../hooks/useMemberOptions'
import { ReviewersFields } from 'components/ReviewsWizard/AddReviewers/ReviewersFields'
import { ReviewerRole } from 'components/ReviewsWizard/types'
import { Plural, ReviewMembershipType } from 'types'
import { SourceDataProvider } from 'components/ReviewsWizard/SourceDataProvider/SourceDataProvider'

interface Props {
  initialViewers: TemplateFormData['viewers']
  initialApprovers: TemplateFormData['approvers']
  isDisabled: boolean
}

export const MemberSelectors = ({ initialViewers, initialApprovers, isDisabled }: Props) => {
  const sourceData = useMemberOptions()
  const form = useFormikContext<TemplateFormData>()
  const initiallyShowViewers = initialViewers.length > 0
  const initiallyShowApprovers = initialApprovers.length > 0
  const initiallyShowReviewers = !initiallyShowViewers && !initiallyShowApprovers
  const initialRoles = compact([
    initiallyShowReviewers ? ReviewerRole.REVIEWER : null,
    initiallyShowViewers ? ReviewerRole.VIEWER : null,
    initiallyShowApprovers ? ReviewerRole.APPROVER : null,
  ])
  const [roles, setRoles] = useState(initialRoles)
  const showViewers = roles.includes(ReviewerRole.VIEWER)
  const showReviewers = roles.includes(ReviewerRole.REVIEWER)
  const showApprovers = roles.includes(ReviewerRole.APPROVER)

  const handleChange = (type: Plural<ReviewMembershipType>) => (values: RecipientSelectorValue[]) => {
    form.setFieldValue(type, values)
  }

  return (
    <SourceDataProvider sourceData={() => sourceData}>
      <ReviewersFields
        autoFocus={false}
        roles={roles}
        onReviewersChange={handleChange('reviewers')}
        onApproversChange={handleChange('approvers')}
        onViewersChange={handleChange('viewers')}
        reviewers={form.values.reviewers}
        approvers={form.values.approvers}
        viewers={form.values.viewers}
        reviewerRolesButtons={
          !showApprovers || !showViewers ? (
            <>
              <div></div>
              <div>
                {!showApprovers && (
                  <Button
                    size="md"
                    variant="tertiary"
                    frontIcon={PlusIcon}
                    aria-label="Add approver"
                    onPress={() => setRoles((roles) => [...roles, ReviewerRole.APPROVER])}
                    isDisabled={isDisabled}
                  >
                    Approver
                  </Button>
                )}
                {!showReviewers && (
                  <Button
                    size="md"
                    variant="tertiary"
                    frontIcon={PlusIcon}
                    aria-label="Add reviewer"
                    onPress={() => setRoles((roles) => [...roles, ReviewerRole.REVIEWER])}
                    isDisabled={isDisabled}
                  >
                    Reviewer
                  </Button>
                )}
                {!showViewers && (
                  <Button
                    size="md"
                    variant="tertiary"
                    frontIcon={PlusIcon}
                    aria-label="Add viewer"
                    onPress={() => setRoles((roles) => [...roles, ReviewerRole.VIEWER])}
                    isDisabled={isDisabled}
                  >
                    Viewer
                  </Button>
                )}
              </div>
            </>
          ) : (
            <></>
          )
        }
      />
    </SourceDataProvider>
  )
}
